// Components
// Libs
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import Snack from '@/common/components/molecules/Snack/Snack';
// Constants
import { ITEM_TYPES, ROUTE_QUOTES } from '@/common/constants';
// Contexts
import { useAddToContext } from '@/common/contexts/AddToContext';
import { useAuth } from '@/common/contexts/AuthContext';
// API
import postDraft from '@/ebat/api/quotes/draft/postDraft';
// Helpers
import formatItemToQuoteType from '@/ebat/helpers/quotes/formatItemToQuoteType';

const useQuotesCreateDraft = ({ disabled, isLoading, isSuccess }) => {
  const { t } = useTranslation();
  const { zergRush } = useAuth();
  const history = useHistory();
  const {
    addToDraftQuote,
    isLoading: isLoadingAddToQuote,
    isSuccess: isSuccessAddToQuote,
  } = useAddToContext();

  /**
   * Mutation to post quote draft
   */
  const {
    mutate: mutatePostQuotesDraft,
    isLoading: isLoadingPostQuotesDraft,
    isSuccess: isSuccessPostQuotesDraft,
  } = useMutation(postDraft);

  /**
   * Callback given to the children component to post the draft
   * @type {function({draft: *}): void}
   */
  const onCreateQuoteDraft = useCallback(
    ({ onSuccess, reference, worksiteCode }) => {
      mutatePostQuotesDraft(
        { reference, worksiteCode },
        {
          onSuccess: (data) => {
            onSuccess(data);

            toast.success(
              <Snack message={t('common:messages:forms.success.default')} />,
            );
          },
          onError: (error) => {
            if (error.response.status === 500) {
              toast.error(
                <Snack
                  message={t('common:messages:forms.errors.server_error')}
                />,
              );
            } else {
              toast.error(
                <Snack message={t('common:messages:forms.errors.default')} />,
              );
            }
          },
        },
      );
    },
    [mutatePostQuotesDraft, t],
  );

  /**
   * Add collection of items to the new quote with items formatted
   * @param {object} draft - new draft quote
   * @param {array} items - array of items to add to the new quote
   * @param {string} source - source of items to format item before send it
   */
  const addItemsToNewQuoteDraft = useCallback(
    ({ draft, items, source }) => {
      const filteredItems = items
        .filter((item) => item.is_selected)
        .map((item) =>
          formatItemToQuoteType({
            item,
            source,
            destination: ITEM_TYPES.draft,
          }),
        );

      addToDraftQuote({
        draft,
        items: filteredItems,
        onSuccessCallback: () => history.push(`${ROUTE_QUOTES}/${draft.id}`),
        options: {}, // to set if needed
      });
    },
    [addToDraftQuote, history],
  );

  /**
   * Create new quote draft an give the onSuccess callback to add items
   * @param {array} items - array of items to add to the new quote
   * @param {string} reference - reference from cart to the new quote
   * @param {string} worksiteCode - code from cart worksite
   * @param {string} source - source of items to format item before send it
   */
  const onCreateQuoteDraftWithItems = useCallback(
    ({ items, reference, worksiteCode, source }) => {
      onCreateQuoteDraft({
        onSuccess: (draft) => addItemsToNewQuoteDraft({ draft, items, source }),
        reference,
        worksiteCode,
      });
    },
    [addItemsToNewQuoteDraft, onCreateQuoteDraft],
  );

  /**
   * Get isSuccess value
   */
  const getIsSuccess = useMemo(
    () => isSuccess || isSuccessAddToQuote || isSuccessPostQuotesDraft,
    [isSuccess, isSuccessAddToQuote, isSuccessPostQuotesDraft],
  );

  /**
   * Check if action is disabled
   * @type {boolean}
   */
  const isDisabled = useMemo(
    () => ({
      loading: isLoading || isLoadingAddToQuote || isLoadingPostQuotesDraft,
      disabled:
        isLoading ||
        zergRush ||
        disabled ||
        isLoadingAddToQuote ||
        isLoadingPostQuotesDraft,
    }),
    [
      disabled,
      isLoading,
      isLoadingAddToQuote,
      isLoadingPostQuotesDraft,
      zergRush,
    ],
  );

  return {
    isDisabled,
    isSuccess: getIsSuccess,
    onCreateQuoteDraft,
    onCreateQuoteDraftWithItems,
  };
};

export default useQuotesCreateDraft;
